import {
  type IntegrationMetadata,
  useGetDemoQuery,
  usePostEventMutation,
} from '@carboncopyco/data';
import {
  EventAction,
  EventTarget,
  type IDemo,
  type IPresentation,
  type IVariable,
  type Opportunity,
  type Play,
} from '@carboncopyco/types';
import { Debug } from '@carboncopyco/utils';
import { useEffect, useRef } from 'react';
import { array, object, string } from 'yup';
import { ValidationErrorMessages } from '../../consts';
import { useFilesServiceContext } from '../../utils';
import {
  GiveADemoFlowModal,
  getAssetType,
  getEntityTitle,
  getEntityType,
} from '../GiveADemoFlowModal';

const debug = Debug.extend('give-a-demo-modal');

/** @deprecated
 * GiveADemoModal is deprecated in favor of GiveADemoFlowModal, which is shared across Demostack apps.
 * Any new implementation should use the new modal only.
 * */

export type PreviewData = {
  integrationMetadata?: IntegrationMetadata;
  client: string;
  from?: 'archive' | 'presentation';
  presentationPreviewId?: string;
};
export type GoToDemoPreviewArgs = {
  previewData?: PreviewData;
  firstUrl?: string;
  presentationId?: string | number;
  presentation?: IPresentation;
  demo?: IDemo;
};

export const getValidationSchema = (opportunityFieldEnabled: boolean) =>
  opportunityFieldEnabled
    ? object().shape({
        client: string().required(ValidationErrorMessages.required),
        variables: array().optional(),
        opportunity: object({
          name: string().required(ValidationErrorMessages.required),
        }),
      })
    : object().shape({
        client: string().required(ValidationErrorMessages.required),
        variables: array().optional(),
      });

type GiveADemoModalProps = {
  onClose: () => void;
  open: boolean;
  organizationId: string;
  workspaceId: string;
  demoId: string;
  goToPresentationPage?: (play: Play) => void;
  goToDemoPreview?: (options?: GoToDemoPreviewArgs) => void;
  goToDemoPresentations?: () => void;
  onSavePresentationSuccess?: (presentation: IPresentation) => void;
  desktopDomain?: string;
};

export type GiveADemoFormState = {
  client: string;
  variables: IVariable[];
  opportunity: Opportunity | null;
};

export const GiveADemoModal = ({
  onClose,
  open,
  organizationId,
  demoId,
  goToDemoPresentations,
  goToPresentationPage,
  workspaceId,
}: GiveADemoModalProps) => {
  const { filesServiceDomain } = useFilesServiceContext();
  const { data: demo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: demoId,
  });
  const [postEvent] = usePostEventMutation();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  return (
    <>
      {demo && (
        <GiveADemoFlowModal
          workspaceId={workspaceId}
          open={open}
          asset={{
            name: getEntityTitle(demo) ?? '',
            type: getAssetType(demo),
            id: String(demo.id),
            assets: [
              {
                assetId: String(demo.id),
                assetRevisionId: demo.revisionId,
                assetType: getEntityType(demo),
                autoSync: false,
                entity: demo,
              },
            ],
          }}
          onClose={() => {
            onClose?.();
          }}
          onPresent={async (data) => {
            goToPresentationPage?.(data);
            postEvent({
              organizationId,
              demoId: String(demoId),
              action: EventAction.STARTED,
              target: EventTarget.PRESENTATION,
            })
              .unwrap()
              .catch((error) => debug(error));
            onClose();
          }}
          onSave={() => {
            goToDemoPresentations();
            onClose();
          }}
          filesServiceUrl={filesServiceDomain}
        />
      )}
    </>
  );
};
